import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formaterDate, formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { LienFiche } from '../composants/LienAction';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { Icone } from '../composants/Icone';
import { ModaleMandat } from '../composants/ModaleMandat';
import { useActionsMandat, type MandatActionnable } from '../composants/ActionsMandat';
import {
  BarreFiltres,
  FiltreChoix,
  FiltreRecherche,
  correspond,
  optionsDistinctes,
} from '../composants/Filtres';

interface Mandat extends MandatActionnable {
  dateActivation: string | null;
  client: { nom: string; prenom: string; reference: string };
  agence: { code: string; libelle: string };
  profil: { id: string; code: string; libelle: string };
  profilAncien: boolean;
  sansMandatSigne: boolean;
}

/** Points de vigilance de la conformité, comptés par le tableau de bord. */
const LIBELLE_VIGILANCE: Record<string, string> = {
  'profil-ancien': 'Profil de plus de 24 mois',
  'sans-piece': 'Mandat scanné manquant',
};

function vigilant(m: Mandat, vigilance: string): boolean {
  if (vigilance === 'profil-ancien') return m.profilAncien;
  if (vigilance === 'sans-piece') return m.sansMandatSigne;
  return true;
}

export const TON_STATUT_MANDAT: Record<string, TonPastille> = {
  BROUILLON: 'neutre',
  EN_ATTENTE_VALIDATION: 'attente',
  ACTIF: 'succes',
  SUSPENDU: 'attente',
  EN_PREAVIS: 'attente',
  EN_LIQUIDATION: 'attente',
  CLOTURE: 'neutre',
  ANNULE: 'refus',
};

export const LIBELLE_STATUT_MANDAT: Record<string, string> = {
  BROUILLON: 'Brouillon',
  EN_ATTENTE_VALIDATION: 'À valider',
  ACTIF: 'Actif',
  SUSPENDU: 'Suspendu',
  EN_PREAVIS: 'En préavis',
  EN_LIQUIDATION: 'En liquidation',
  CLOTURE: 'Clôturé',
  ANNULE: 'Annulé',
};

export function Mandats() {
  const { donnees, chargement, erreur, recharger } = useRessource<Mandat[]>('/mandats');
  const tous = donnees ?? [];
  const aValider = tous.filter((m) => m.statut === 'EN_ATTENTE_VALIDATION').length;

  // Une tuile du tableau de bord arrive filtrée : statut, profil, agence ou
  // point de vigilance.
  const [requete] = useSearchParams();
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState(() => requete.get('statut') ?? '');
  const [profil, setProfil] = useState(() => requete.get('profil') ?? '');
  const [agence, setAgence] = useState(() => requete.get('agence') ?? '');
  const [vigilance, setVigilance] = useState(() => requete.get('vigilance') ?? '');
  const filtresActifs =
    recherche !== '' || statut !== '' || profil !== '' || agence !== '' || vigilance !== '';
  const mandats = tous.filter(
    (m) =>
      correspond(recherche, m.reference, m.client.nom, m.client.prenom, m.client.reference) &&
      (!statut || m.statut === statut) &&
      (!profil || m.profil.id === profil) &&
      (!agence || m.agence.code === agence) &&
      vigilant(m, vigilance),
  );

  function retirerFiltres() {
    setRecherche('');
    setStatut('');
    setProfil('');
    setAgence('');
    setVigilance('');
  }
  const { aRole } = useAuth();
  const peutSaisir = aRole('MIDDLE_BO', 'RESP_GESTION', 'ADMIN');
  const naviguer = useNavigate();
  const [ouverte, setOuverte] = useState(false);
  const actions = useActionsMandat({ surChangement: recharger });

  const colonnes: Colonne<Mandat>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      num: true,
      rendu: (m) => <LienFiche to={`/mandats/${m.id}`}>{m.reference}</LienFiche>,
    },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (m) => formaterIdentite(m.client.nom, m.client.prenom),
    },
    { cle: 'profil', entete: 'Profil', rendu: (m) => m.profil.libelle },
    { cle: 'agence', entete: 'Agence', rendu: (m) => m.agence.code },
    {
      cle: 'activation',
      entete: 'Activation',
      num: true,
      rendu: (m) => formaterDate(m.dateActivation),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (m) => (
        <Pastille ton={TON_STATUT_MANDAT[m.statut] ?? 'neutre'}>
          {LIBELLE_STATUT_MANDAT[m.statut] ?? m.statut}
        </Pastille>
      ),
    },
    {
      cle: 'actions',
      entete: <span className="visuellement-cache">Actions</span>,
      num: true,
      rendu: (m) => actions.menu(m),
    },
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Mandats</h1>
        <span className="entete__ref">{tous.length} mandats</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {actions.erreur}

      {aValider > 0 && (
        <BandeauAlerte nature="gestion" titre="En attente de validation">
          {aValider} mandat(s) attendent une seconde paire d’yeux. Le validateur ne peut pas être
          celui qui a saisi le dossier.{' '}
          <button
            type="button"
            className="bouton bouton--fantome bouton--compact"
            onClick={() => {
              retirerFiltres();
              setStatut('EN_ATTENTE_VALIDATION');
            }}
          >
            Les afficher
          </button>
        </BandeauAlerte>
      )}

      <Bloc
        titre="Portefeuille de mandats"
        meta={
          chargement
            ? 'chargement…'
            : filtresActifs
              ? `${mandats.length} sur ${tous.length}`
              : undefined
        }
        actions={
          peutSaisir && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={() => setOuverte(true)}
            >
              <Icone nom="creer" taille={15} />
              Nouveau mandat
            </button>
          )
        }
      >
        <BarreFiltres actifs={filtresActifs} onRetirer={retirerFiltres}>
          <FiltreRecherche
            valeur={recherche}
            onChange={setRecherche}
            placeholder="Référence, nom ou prénom du client"
          />
          <FiltreChoix
            libelle="Statut"
            tous="Tous les statuts"
            valeur={statut}
            onChange={setStatut}
            options={Object.entries(LIBELLE_STATUT_MANDAT)}
          />
          <FiltreChoix
            libelle="Profil"
            tous="Tous les profils"
            valeur={profil}
            onChange={setProfil}
            options={optionsDistinctes(tous, (m) => [m.profil.id, m.profil.libelle])}
          />
          <FiltreChoix
            libelle="Agence"
            tous="Toutes les agences"
            valeur={agence}
            onChange={setAgence}
            options={optionsDistinctes(tous, (m) => [
              m.agence.code,
              `${m.agence.code} — ${m.agence.libelle}`,
            ])}
          />
          <FiltreChoix
            libelle="Vigilance"
            tous="Sans critère de vigilance"
            valeur={vigilance}
            onChange={setVigilance}
            options={Object.entries(LIBELLE_VIGILANCE)}
          />
        </BarreFiltres>
        <Tableau
          colonnes={colonnes}
          lignes={mandats}
          cle={(m) => m.id}
          vide={filtresActifs ? 'Aucun mandat ne répond à ces critères.' : 'Aucun mandat.'}
        />
      </Bloc>

      <ModaleMandat
        ouverte={ouverte}
        onFermer={() => setOuverte(false)}
        // La suite du dossier (mandat signé, soumission) se fait sur la fiche
        // du mandat : on y conduit directement.
        onCree={(m) => naviguer(`/mandats/${m.id}`)}
      />

      {actions.modales}
    </>
  );
}
